/**
 * The frame every step of Make a room sits in on a phone and an iPad: a phone is one question on a screen with its one
 * action docked; upright is the step's card beside the seven (or four) steps; on its side the steps are the list pane
 * and the card is the main pane. What each step shows is the same as the computer's, from the same hooks.
 */
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { DockedBar } from "../../components/season/DockedBar";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { Pane, Panes } from "../../components/season/Panes";
import { PushedNav } from "../../components/season/PushedNav";
import { SeasonCard } from "../../components/season/Card";
import { BOARD_STEPS, LIVE_STEPS } from "../progress";
import "../../styles/season-make.css";

export type MakeKind = "rec" | "live";

function Steps({ kind, current, reached }: { kind: MakeKind; current: number; reached: number }) {
  const labels = (kind === "live" ? LIVE_STEPS : BOARD_STEPS).map((s) => s.label);
  return (
    <ol className="k-gl k-nolead mk-steps" aria-label="The steps">
      {labels.map((label, i) => {
        const done = i < reached;
        return (
          <li className="k-lr" key={label}>
            <div className={`k-lr-b${i === current ? " on" : ""}`} {...(i === current ? { "aria-current": "step" as const } : {})}>
              <span className={`mk-n${done ? " ok" : i === current ? " on" : ""}`}>{done ? <Icon name="check" /> : i + 1}</span>
              <span className="k-lr-t">
                <b>{label}</b>
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function BackLink({ back }: { back: { to: string; label: string; onBack?: () => void } }) {
  return (
    <Link
      to={back.to}
      className="k-back"
      onClick={(e) => {
        if (!back.onBack) return;
        e.preventDefault();
        back.onBack();
      }}
    >
      <Icon name="left" />
      <span>{back.label}</span>
    </Link>
  );
}

interface FrameProps {
  layout: SeasonLayout;
  kind: MakeKind;
  /** The step on screen, and the furthest the draft has reached. */
  current: number;
  reached: number;
  /** "Step 3 of 7", above the question. */
  kicker?: string;
  title: ReactNode;
  back: { to: string; label: string; onBack?: () => void };
  /** A draft exists: Finish later saves it and goes back to Rooms. */
  onFinishLater?: () => void;
  /** The step's one action: docked on a phone, in the card on an iPad. */
  action?: ReactNode;
  children: ReactNode;
}

export function SeasonMakeFrame({ layout, kind, current, reached, kicker, title, back, onFinishLater, action, children }: FrameProps) {
  const heading = kind === "live" ? "A new live room" : "A new room from a recording";
  const later = onFinishLater ? (
    layout === "phone" ? (
      <button type="button" className="k-link" onClick={onFinishLater}>
        <Icon name="clock" /> Finish later
      </button>
    ) : (
      <Button icon="clock" onClick={onFinishLater}>
        Finish later
      </Button>
    )
  ) : null;
  if (layout === "phone") {
    return (
      <Page layout="phone" className="mk">
        <PushedNav back={back} right={later} />
        {kicker ? <p className="mk-kick">{kicker}</p> : null}
        <h1 className="k-h1 mk-h1">{title}</h1>
        {children}
        {action ? <DockedBar>{action}</DockedBar> : null}
      </Page>
    );
  }
  const card = (
    <SeasonCard className="mk-main">
      <div className="mk-mh">
        {kicker ? <p className="mk-kick">{kicker}</p> : null}
        <h2 className="k-h1 mk-h1">{title}</h2>
      </div>
      {children}
      {action ? <div className="mk-cf">{action}</div> : null}
    </SeasonCard>
  );
  if (layout === "upright") {
    return (
      <Page layout="upright" className="mk">
        <div className="k-head mk-head">
          <div>
            <BackLink back={back} />
            <h1 className="k-title">{heading}</h1>
          </div>
          {later}
        </div>
        <div className="k-two mk-two">
          {card}
          <aside className="k-pane mk-sp">
            <Steps kind={kind} current={current} reached={reached} />
          </aside>
        </div>
      </Page>
    );
  }
  return (
    <Panes className="mk-split">
      <Pane kind="list" label="The steps">
        <div className="k-sp-lh">
          <BackLink back={back} />
          <h1 className="k-title">{heading}</h1>
        </div>
        <Page layout="side" className="mk mk-lp">
          <Steps kind={kind} current={current} reached={reached} />
          {later ? <div className="mk-lf">{later}</div> : null}
        </Page>
      </Pane>
      <Pane kind="main" label={heading}>
        <Page layout="side" className="mk mk-center">
          {card}
        </Page>
      </Pane>
    </Panes>
  );
}
